import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Button,
    Card,
    CardBody,
    CardHeader,
    Carousel,
    CarouselControl,
    CarouselIndicators,
    CarouselItem,
    Col,
    Label,
    Row,
} from "reactstrap";
import { customerIdData } from "./constants/constantFunctionsView";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Loader } from "../Utils/Loader";
import { useDispatch, useSelector } from "react-redux";
import { LabelText } from "../Utils/Input";
import InputField from "../Utils/InputField";
import { fetchAllSessions } from "../../slices/sessions/getAllSessions/thunk";
import { grantData, sessionJsonAndApiCall } from "./constants/SessionFunctions";
import { goToIndex, next, previous } from "./constants/corouselNavigation";
import { fetchCustomerSummary } from "../../slices/customer/simulate/thunk";
import { ResourceBalance } from "./ResourceBalance";

export const ActiveSessionBalance = ({customerId, isRefreshing, setIsRefreshing}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [animating, setAnimating] = useState(false);
    const [chunkSize, setChunkSize] = useState(3); // Default chunk size
    let simulateOrPreview = "";

    // Function to determine chunk size based on screen width
    const getChunkSize = () => {
        const width = window.innerWidth;
        if (width < 576) return 1; // Extra small screens - 1 item per slide
        if (width < 768) return 2; // Small screens - 2 items per slide  
        return 3; // Large screens and above - 3 items per slide
    };

    useEffect(() => {
        const handleResize = () => {
            const newChunkSize = getChunkSize();
            if (newChunkSize !== chunkSize) {
                setChunkSize(newChunkSize);
                setActiveIndex(0);
            }
        };
        setChunkSize(getChunkSize());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [chunkSize]);

    const makeApiCalls = async () => {
        if (customerId) {
            simulateOrPreview = "View";
            setLoading(true);
            try {
                await Promise.all([
                    dispatch(fetchCustomerSummary(customerId, setLoading, simulateOrPreview)),
                    dispatch(fetchAllSessions(customerId))
                ]);
            } catch (error) {
                console.error('API calls failed:', error);
            } finally {
                setLoading(false);
                // Only set isRefreshing to false after API calls complete
                if (isRefreshing) {
                    setIsRefreshing(false);
                }
            }
        }
    };

    // Initial load
    useEffect(() => {
        makeApiCalls();
    }, [customerId]);

    // Handle refresh
    useEffect(() => {
        if (isRefreshing) {
            makeApiCalls();
        }
    }, [isRefreshing]);

    const fetchCustomerSummaryData = useSelector((state) => state.Simulate.simulate);
    
    const formatCarouselData = (data) => {
        return data?.map((item) => ({
            ...item, // Keep all original properties
            resourceName: item.resourceName,
            consumedBalance: item?.consumedBalance || 0,
            remainingBalance: item?.remainingBalance || (item.originalBalance - (item?.consumedBalance || 0)),
            reservedBalance: item?.reservedBalance || 0,
            currency: item.currencySymbol || "",
            uom: item.uom || item.currencyCode || ''
        }));
    };

    // Helper function to chunk array into groups based on responsive chunk size
    const chunkArray = (array, chunkSize) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    };

    const allResources = formatCarouselData(fetchCustomerSummaryData?.resources || []);
    
    if (!allResources || allResources.length === 0) {
        return null; 
    }

    // If resources are less than or equal to chunk size, show all in one card without carousel
    if (allResources.length <= chunkSize) {
        return (
            <Card className="border rounded-1 px-2 py-2 my-2 bg-white">
                {loading && <Loader />}
                <CardBody>
                    <ResourceBalance resources={allResources} showAll={true} />
                </CardBody>
            </Card>
        );
    }

    // If more resources than chunk size, use carousel with responsive chunking
    const resourceChunks = chunkArray(allResources, chunkSize);

    return (
        <Card className="border rounded-1 px-2 py-2 my-2 bg-white">
            {loading && <Loader />}
            <div>
                <CardBody>
                <div style={{ position: 'relative', width: '100%' }}>
                    <Carousel
                        activeIndex={activeIndex}
                        next={() =>
                            next(resourceChunks, activeIndex, setActiveIndex, animating)
                        }
                        previous={() =>
                            previous(resourceChunks, activeIndex, setActiveIndex, animating)
                        }
                        interval={false}
                        className="w-100"
                    >
                        {/* <CarouselIndicators
                            items={resourceChunks}
                            activeIndex={activeIndex}
                            onClickHandler={(newIndex) =>
                                goToIndex(newIndex, animating, setActiveIndex)
                            }
                        /> */}
                        {resourceChunks.map((chunk, idx) => (
                            <CarouselItem
                                onExiting={() => setAnimating(true)}
                                onExited={() => setAnimating(false)}
                                key={idx}
                                className="w-100"
                            >
                            <div className="w-100">
                                <ResourceBalance resources={chunk} showAll={true} />
                            </div>
                            </CarouselItem>
                        ))}
                        <CarouselControl
                            className="carousel-prev-custom carousel-width ms-n4"
                            direction="prev"
                            directionText="Previous"
                            onClickHandler={() =>
                                previous(resourceChunks, activeIndex, setActiveIndex, animating)
                            }
                        />
                        <CarouselControl
                            className="carousel-next-custom carousel-width me-2"
                            direction="next"
                            directionText="Next"
                            onClickHandler={() =>
                                next(resourceChunks, activeIndex, setActiveIndex, animating)
                            }
                        />
                    </Carousel>
                </div>
                </CardBody>
            </div>
        </Card>
    );
};